import { ReactNode } from "react";
import { cn } from "../../utils/class-names";
import { ProtocolKey } from "@silo/types";
import { match } from "ts-pattern";
import { tv } from "tailwind-variants";

export const sortButton = tv({
  base: "hover:text-white hover:ease-in duration-100",
  variants: {
    isActive: {
      true: "text-white font-medium",
    },
  },
});

interface BaseRowProps {
  className?: string;
  children?: ReactNode;
  ["data-cy"]?: string;
}

export function BaseRow({
  className,
  children,
  ["data-cy"]: dataCy,
}: BaseRowProps): JSX.Element {
  return (
    <div
      className={cn("flex items-center gap-2 px-5 py-3", className)}
      data-cy={dataCy}
    >
      {children}
    </div>
  );
}

interface MarketCellProps {
  className?: string;
  children?: ReactNode;
}

export function MarketCell({
  className,
  children,
}: MarketCellProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 lg:gap-4 min-w-[25%] lg:min-w-[20%]",
        className
      )}
    >
      {children}
    </div>
  );
}

interface LiquidityCellProps {
  className?: string;
  children?: ReactNode;
}

export function LiquidityCell({
  className,
  children,
}: LiquidityCellProps): JSX.Element {
  return (
    <div className={cn("min-w-[25%] flex justify-end", className)}>
      {children}
    </div>
  );
}

interface MarketYieldsCellProps {
  protocolKey: ProtocolKey;
  className?: string;
  children?: ReactNode;
}

export function MarketYieldsCell({
  protocolKey,
  className,
  children,
}: MarketYieldsCellProps): JSX.Element {
  return (
    <div
      className={cn(
        "grid gap-2 w-full justify-items-end",
        match(protocolKey)
          .with("anvil", () => "grid-cols-3")
          .with("arbitrum", () => "grid-cols-3")
          .with("base", () => "grid-cols-3")
          .with("lrtbase", () => "grid-cols-2")
          .with("local", () => "grid-cols-2")
          .with("mainnetGrand", () => "grid-cols-3")
          .with("mainnetLegacy", () => "grid-cols-3")
          .with("mainnetLlama", () => "grid-cols-2")
          .with("optimism", () => "grid-cols-3")
          .exhaustive(),
        className
      )}
    >
      {children}
    </div>
  );
}
